import {useCallback, useMemo, useState} from 'react';

import {Box, Divider} from '@mui/material';

import {MatchFilterOptions, MatchFilters} from '../MatchFilters/MatchFilters';
import {filterBoardColumnsBySearchTerm} from '../utils/matchesBoardUtils';

import {LoadingContainer} from '../../../../../components/Loading/LoadingContainer';
import {BoardColumnData, BoardColumnKey} from '../../../../../hooks/useMatchesBoardColumnsSorting';
import {orangeColors} from '../../../../../layouts/theme/designSystem';
import {Option} from '../../../../../types/shared-types';
import {BoardView} from '../../applicants/components/BoardView/BoardView';
import {matchesBoard} from '../../applicants/components/BoardView/MatchesBoard.colors';

const MERGE_IN_PROGRESS_STORAGE_KEY = 'matchesPage:mergeInProgressColumns';

interface Props {
  loadingInitialData: boolean;
  filterBy: (filter: MatchFilterOptions) => void;
  jobOptions: Option[];
  statusOptions: Option[];
  initialJobs: Option[];
  initialStatuses: Option[];
  columns: Record<BoardColumnKey, BoardColumnData>;
  searchTerm: string;
  onSearchTermChange: (value: string) => void;
}

export const MatchesBoardView = ({
  loadingInitialData,
  filterBy,
  jobOptions,
  statusOptions,
  initialJobs,
  initialStatuses,
  columns,
  searchTerm,
  onSearchTermChange,
}: Props) => {
  const handleBoardViewSuccess = () => undefined;

  const [mergeInProgressColumns, setMergeInProgressColumns] = useState(() => {
    if (typeof window === 'undefined') {
      return false;
    }
    return window.localStorage.getItem(MERGE_IN_PROGRESS_STORAGE_KEY) === '1';
  });

  const handleMergeInProgressColumnsChange = useCallback((merged: boolean) => {
    setMergeInProgressColumns(merged);
    window.localStorage.setItem(MERGE_IN_PROGRESS_STORAGE_KEY, merged ? '1' : '0');
  }, []);

  const filteredColumns = useMemo((): Record<BoardColumnKey, BoardColumnData> => {
    return filterBoardColumnsBySearchTerm(columns, searchTerm);
  }, [columns, searchTerm]);
  const totalLoadedMatches =
    filteredColumns.new.matches.length +
    filteredColumns.contacted.matches.length +
    filteredColumns.interview.matches.length;

  return (
    <Box
      sx={{
        bgcolor: matchesBoard.surfaceBackground,
        borderRadius: '16px',
        boxShadow: matchesBoard.surfaceShadow,
        px: {xs: 2, sm: 3},
        pt: 3,
        pb: 4,
        overflow: 'hidden',
      }}
    >
      {loadingInitialData ? (
        <LoadingContainer minHeight='50vh' sx={{minHeight: {xs: '30vh', sm: '40vh', md: '50vh'}}} />
      ) : (
        <>
          <Box data-tour='matches.filters'>
            <MatchFilters
              filterBy={filterBy}
              jobOptions={jobOptions}
              statusOptions={statusOptions}
              initialJobs={initialJobs}
              initialStatuses={initialStatuses}
              searchTerm={searchTerm}
              onSearchTermChange={onSearchTermChange}
            />
          </Box>
          <Divider sx={{my: 2.5, borderColor: orangeColors[3]}} />
          <Box data-tour='matches.board'>
            <BoardView
              columns={filteredColumns}
              selectedFilter={null}
              onSuccess={handleBoardViewSuccess}
              totalMatches={totalLoadedMatches}
              mergeInProgressColumns={mergeInProgressColumns}
              onMergeInProgressColumnsChange={handleMergeInProgressColumnsChange}
            />
          </Box>
        </>
      )}
    </Box>
  );
};
